<template>
  <div id="modalCreateEditFinancialResource" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ textModal }} Recurso</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            title="Fechar"
            @click="$emit('closeModal', false)"
          />
        </div>
        <form @submit.prevent="save">
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-sm-8">
                <label class="form-label">
                  Título: <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.resource.title.$errors.length }"
                  placeholder="Título"
                  v-model="v$.resource.title.$model"
                />
                <span
                  v-if="v$.resource.title.required.$invalid"
                  class="invalid-feedback"
                >
                  O campo é obrigatório
                </span>
              </div>
              <div class="col-sm-4">
                <label class="form-label">
                  Tipo: <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  :class="{ 'is-invalid': v$.resource.type.$errors.length }"
                  :disabled="financialResourceId"
                  v-model="v$.resource.type.$model"
                >
                  <option value="">Selecione o tipo do recurso</option>
                  <option value="Recursos Financeiros">
                    Recursos Financeiros
                  </option>
                  <option value="Recursos Financeiros Aportados">
                    Recursos Financeiros Aportados
                  </option>
                </select>
                <span
                  v-if="v$.resource.type.required.$invalid"
                  class="invalid-feedback"
                >
                  O campo é obrigatório
                </span>
              </div>
              <div class="col-sm-12">
                <label class="form-label">
                  Detalhamento: <span class="text-danger">*</span>
                </label>
                <textarea
                  class="form-control"
                  :class="{
                    'is-invalid': v$.resource.description.$errors.length,
                  }"
                  placeholder="Detalhamento"
                  rows="5"
                  v-model="v$.resource.description.$model"
                />
                <span
                  v-if="v$.resource.description.required.$invalid"
                  class="invalid-feedback"
                >
                  O campo é obrigatório
                </span>
              </div>
              <hr class="my-3" />
              <div class="row">
                <div
                  class="col-lg-6 border-end border-end-width-1 border-end-secondary"
                >
                  <p class="text-center"><strong>Proponentes</strong></p>
                  <div class="row">
                    <div
                      v-for="bidder in resource.bidders"
                      :key="bidder.id"
                      class="d-flex flex-column"
                      :class="{ 'col-lg-6': resource.bidders.length > 1 }"
                    >
                      <label class="col-form-label text-center">
                        {{ bidder.name }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm form-control-number text-center"
                        step="0.01"
                        placeholder="Valor"
                        v-model="bidder.value"
                      />
                    </div>
                  </div>
                </div>
                <div
                  class="col-lg-6 border-start border-start-width-1 border-start-secondary"
                >
                  <p class="text-center"><strong>Parceiros</strong></p>
                  <div class="row">
                    <div
                      v-for="partner in resource.partners"
                      :key="partner.id"
                      class="d-flex flex-column"
                      :class="{
                        'col-lg-6': resource.partners.length > 1,
                      }"
                    >
                      <label class="col-form-label text-center">
                        {{ partner.name }}
                      </label>
                      <input
                        type="number"
                        class="form-control form-control-sm form-control-number text-center"
                        step="0.01"
                        placeholder="Valor"
                        v-model="partner.value"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top-0">
            <button
              type="button"
              class="btn btn-sm btn-link"
              data-bs-dismiss="modal"
              title="Cancelar"
              @click="$emit('closeModal', false)"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="btn btn-sm btn-primary"
              :title="textModal"
              :disabled="v$.$invalid || disableButton"
            >
              <span v-if="disableButton">
                <i class="ph-spinner spinner" />
              </span>
              <span v-else>{{ textModal }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { showModal, hideModal } from '@/utils/modal'

export default {
  name: 'CreateEditFinancialResourceModal',

  props: {
    projectId: {
      type: String,
      required: true,
    },
    financialResourceId: {
      type: String,
      default: null,
    },
  },

  setup() {
    return { v$: useVuelidate() }
  },

  data() {
    return {
      resource: {
        title: '',
        type: '',
        description: '',
      },
      disableButton: false,
    }
  },

  computed: {
    textModal() {
      return this.financialResourceId ? 'Atualizar' : 'Cadastrar'
    },
  },

  methods: {
    async getFinancialResource() {
      try {
        this.resource = await this.$store.dispatch(
          'getProjectFinancialResource',
          {
            projectId: this.projectId,
            financialResourceId: this.financialResourceId,
          },
        )
        showModal('modalCreateEditFinancialResource')
      } catch (error) {
        if (error && error.message) {
          this.$root.$refs.notification.warning(error.message)
        } else {
          this.$root.$refs.notification.error(
            'Não foi possível se conectar ao servidor.',
          )
        }
        this.$emit('closeModal', false)
      }
    },

    async getProjectBiddersPartners() {
      try {
        const bidders = await this.$store.dispatch(
          'getProjectBidders',
          this.projectId,
        )

        const partners = await this.$store.dispatch(
          'getProjectPartners',
          this.projectId,
        )

        this.resource.bidders = bidders.map((bidder) => {
          return {
            projectBidderId: bidder.id,
            name: bidder.bidderPartnerName,
            value: null,
          }
        })

        this.resource.partners = partners.map((bidder) => {
          return {
            projectPartnerId: bidder.id,
            name: bidder.bidderPartnerName,
            value: null,
          }
        })

        showModal('modalCreateEditFinancialResource')
      } catch (error) {
        if (error && error.message) {
          this.$root.$refs.notification.warning(error.message)
        } else {
          this.$root.$refs.notification.error(
            'Não foi possível se conectar ao servidor.',
          )
        }
        this.$emit('closeModal', false)
      }
    },

    async save() {
      if (!this.v$.$invalid) {
        this.disableButton = true
        try {
          let response = null

          if (this.financialResourceId) {
            response = await this.$store.dispatch(
              'updateProjectFinancialResource',
              {
                projectId: this.projectId,
                financialResourceId: this.financialResourceId,
                resource: this.resource,
              },
            )
          } else {
            response = await this.$store.dispatch(
              'saveProjectFinancialResource',
              {
                projectId: this.projectId,
                resource: this.resource,
              },
            )
          }

          hideModal()
          this.$emit('closeModal', true)
          this.$root.$refs.notification.success(response.message)
        } catch (error) {
          this.disableButton = false
          if (error && error.message) {
            this.$root.$refs.notification.warning(error.message)
          } else {
            this.$root.$refs.notification.error(
              'Não foi possível se conectar ao servidor.',
            )
          }
        }
      }
    },
  },

  validations() {
    return {
      resource: {
        title: {
          required,
        },
        type: {
          required,
        },
        description: {
          required,
        },
      },
    }
  },

  mounted() {
    if (this.financialResourceId) {
      this.getFinancialResource()
    } else {
      this.getProjectBiddersPartners()
    }
  },
}
</script>
